import React from 'react';

import { css } from '@compiled/react';
import { BLOCKS, MARKS, type Block, type Inline, type Text } from '@contentful/rich-text-types';

import { media } from '@atlaskit/primitives/responsive';

import { GenericTable } from '@/libraries/design/patterns/table/src/generic-table';
import { GenericTableColumnHeader } from '@/libraries/design/patterns/table/src/generic-table-column-header';
import { GenericTableCustomCell } from '@/libraries/design/patterns/table/src/generic-table-custom-cell';
import {
	GenericTableHeadRow,
	GenericTableThead,
} from '@/libraries/design/patterns/table/src/generic-table-header-row';
import { GenericTableRow } from '@/libraries/design/patterns/table/src/generic-table-row';

/** Tables with at least this many columns (row title column included) span the full width. */
const FULL_WIDTH_MIN_COLUMNS = 4;
/** With this many columns or more, the row title column is capped at ROW_TITLE_MAX_WIDTH. */
const ROW_TITLE_CAP_MIN_COLUMNS = 5;
const ROW_TITLE_MAX_WIDTH = '240px';

type RichTextNode = Block | Inline | Text;

// Column and row titles are bold. Their text is in rich text paragraphs, which global
// typography sets to `p { font-weight: 400 }`; `div p` (RichTextComponent's wrapper) out-ranks
// that selector regardless of stylesheet order.
const titleContentCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& div p': {
		fontWeight: 700,
	},
});

// Up to 3 columns span 8 of the 10 grid columns on desktop, centered
const narrowTableCss = css({
	[media.above.md]: {
		width: '80%',
		marginInline: 'auto',
	},
});

export interface BodyContentTableProps {
	/** A Contentful rich text `table` node. */
	table: Block;
	/** Renders a cell's content (its paragraphs) — body content's rich text rendering. */
	renderCellContent: (cell: Block) => React.ReactNode;
}

const getNodeText = (node: RichTextNode): string =>
	node.nodeType === 'text' ? node.value : node.content.map((child) => getNodeText(child)).join('');

const getCells = (row: Block): Block[] =>
	row.content.filter((cell): cell is Block =>
		[BLOCKS.TABLE_CELL, BLOCKS.TABLE_HEADER_CELL].includes(cell.nodeType as BLOCKS),
	);

const isHeaderCell = (cell: Block | undefined): boolean =>
	cell?.nodeType === BLOCKS.TABLE_HEADER_CELL;

const isEmptyCell = (cell: Block | undefined): boolean => !cell || !getNodeText(cell).trim();

const getTextNodes = (node: RichTextNode): Text[] =>
	node.nodeType === 'text' ? [node] : node.content.flatMap((child) => getTextNodes(child));

/** True when the cell has text and all of it (whitespace aside) is bold, including link text. */
const isBoldCell = (cell: Block | undefined): boolean => {
	const textNodes = cell ? getTextNodes(cell).filter((text) => text.value.trim()) : [];
	return (
		textNodes.length > 0 &&
		textNodes.every((text) => text.marks.some((mark) => mark.type === MARKS.BOLD))
	);
};

/**
 * Renders a Contentful rich text table with GenericTable's v2 styling (light blue header
 * row, bold row titles, equal column widths, mobile column scrolling with arrow controls).
 *
 * - Column titles: a first row of header cells.
 * - Row titles: the first column, when every cell has bold content - exception for first cell as empty
 *   and every other cell in it is bold.
 * - Width: up to 3 columns span 8 of 10 grid columns on desktop; 4+ span all 10.
 */
export const BodyContentTable = ({ table, renderCellContent }: BodyContentTableProps) => {
	const rows = table.content.filter((row): row is Block => row.nodeType === BLOCKS.TABLE_ROW);
	const [firstRow, ...otherRows] = rows;
	const firstRowCells = firstRow ? getCells(firstRow) : [];
	const hasColumnTitles = firstRowCells.length > 0 && firstRowCells.every(isHeaderCell);
	const bodyRows = hasColumnTitles ? otherRows : rows;

	const columnCount = Math.max(0, ...rows.map((row) => getCells(row).length));
	if (!columnCount) {
		return null;
	}

	const [cornerCell] = firstRowCells;
	const hasEmptyCorner = hasColumnTitles && isEmptyCell(cornerCell);
	// The first column: all bold, or an empty top cell with every cell below it bold
	const [topCell, ...belowTopCells] = rows.map((row) => getCells(row)[0]);
	const hasRowTitles =
		bodyRows.length > 0 &&
		belowTopCells.every(isBoldCell) &&
		(isBoldCell(topCell) || (isEmptyCell(topCell) && belowTopCells.length > 0));

	const columnWidth = `${100 / columnCount}%`;
	// The capped row title column frees space, which the other (unsized) columns share
	// equally — table-layout is fixed
	const rowTitleColWidth =
		hasRowTitles && columnCount >= ROW_TITLE_CAP_MIN_COLUMNS
			? `min(${columnWidth}, ${ROW_TITLE_MAX_WIDTH})`
			: undefined;

	const renderTitle = (cell: Block) => <div css={titleContentCss}>{renderCellContent(cell)}</div>;

	return (
		<div css={columnCount < FULL_WIDTH_MIN_COLUMNS && narrowTableCss}>
			<GenericTable variant="v2">
				{hasColumnTitles && (
					<GenericTableThead colWidth={rowTitleColWidth ? undefined : columnWidth}>
						<GenericTableHeadRow>
							{firstRowCells.map((cell, index) => {
								// The empty top-left corner (above the row titles) is blank and hidden
								const isCorner = index === 0 && hasEmptyCorner;
								return (
									<GenericTableColumnHeader
										key={index}
										aria-hidden={isCorner || undefined}
										colWidth={index === 0 ? rowTitleColWidth : undefined}
										// Stays in place on mobile with the row titles below it
										isRowHeaderColumn={hasRowTitles && index === 0}
									>
										{!isCorner && renderTitle(cell)}
									</GenericTableColumnHeader>
								);
							})}
						</GenericTableHeadRow>
					</GenericTableThead>
				)}
				<tbody>
					{bodyRows.map((row, rowIndex) => (
						<GenericTableRow key={rowIndex}>
							{getCells(row).map((cell, cellIndex) => {
								const isRowTitle = hasRowTitles && cellIndex === 0;
								return (
									<GenericTableCustomCell key={cellIndex} isRowHeader={isRowTitle}>
										{isRowTitle ? renderTitle(cell) : renderCellContent(cell)}
									</GenericTableCustomCell>
								);
							})}
						</GenericTableRow>
					))}
				</tbody>
			</GenericTable>
		</div>
	);
};
